import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Save, Package, Plus, Trash2, ArrowLeft, AlertCircle, Search, X, FileText, FileSpreadsheet, Download, FileEdit } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default towel type options
const defaultTowelTypes = {
  piece_dyed: false,
  yarn_dyed: false,
  wash_finish: false,
  loom_state: false,
  dobby: false,
  jacquard: false,
  sheared: false,
  un_sheared: false,
  other: ""
};

// Empty order item row
const emptyOrderItem = {
  design_name: "",
  size_cm: "",
  gsm: "",
  wt_per_pc_gms: "",
  finish_wt_per_pc_gms: "",  // Finish Weight per Piece
  order_pcs: "",
  order_kgs: "",
  total_finish_wt_kgs: "",   // Auto-calculated: (order_pcs × finish_wt_per_pc_gms) / 1000
  pile_count: "",
  ground_count: "",
  weft: "",
  border: "",
  extra_qty: "",  // Changed from "as_per" - auto-adds to plan_pcs
  plan_pcs: "",   // Auto-calculated: order_pcs + extra_qty
  plan_kgs: "",   // Auto-calculated: (plan_pcs × wt_per_pc_gms) / 1000
  remarks: ""
};

// Empty shade row
const emptyShadeItem = {
  shade_no: "",
  size: "",
  wt_per_pc_gms: "",
  finish_wt_per_pc_gms: "",
  order_pcs: "",
  order_kgs: "",           // Auto-calculated: (order_pcs × wt_per_pc_gms) / 1000
  total_finish_wt_kgs: ""  // Auto-calculated: (order_pcs × finish_wt_per_pc_gms) / 1000
};

export default function CreateWorkOrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editWoId = searchParams.get('edit');
  const isEditMode = !!editWoId;
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  
  // Basic info
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_po_number: "",
    shipment_date: "",
    weaving_end_date: "",
    ex_factory_date: "",
    special_instructions: "",
    packing_type: "packed"  // "packed" or "roll_form"
  });
  
  // Type of Towel - Yes/No options
  const [towelTypes, setTowelTypes] = useState({ ...defaultTowelTypes });
  
  // Order Details table (replaces size_items)
  const [orderItems, setOrderItems] = useState([{ ...emptyOrderItem }]);
  
  // Shade details table - hidden by default
  const [shadeItems, setShadeItems] = useState([{ ...emptyShadeItem }]);
  const [showShadeDetails, setShowShadeDetails] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [fetchingWO, setFetchingWO] = useState(false);
  const [error, setError] = useState("");
  
  // Original WO data for edit restrictions
  const [originalWO, setOriginalWO] = useState(null);
  
  // Edit permissions
  const isAdmin = currentUser?.role === 'admin';
  const isWeaving = currentUser?.department?.toLowerCase() === 'weaving';
  
  // Check if WO is "final" (has production entries or shipped)
  const isWOFinal = originalWO?.current_stage !== 'weaving' || 
                    (originalWO?.stage_allocations?.weaving?.total_pieces > 0);
  
  // Weaving can only edit wt_per_pc_gms after WO is final
  const canEditFullWO = isAdmin || !isWOFinal;
  const canOnlyEditWeight = isWeaving && isWOFinal && !isAdmin;
  
  // PO Search state
  const [poSearchQuery, setPoSearchQuery] = useState("");
  const [poSearchResults, setPoSearchResults] = useState([]);
  const [poSearchLoading, setPoSearchLoading] = useState(false);
  const [showPoResults, setShowPoResults] = useState(false);
  const [linkedPO, setLinkedPO] = useState(null);
  
  // Reference WO Search state (for copying existing WO data)
  const [refWoSearchQuery, setRefWoSearchQuery] = useState("");
  const [refWoSearchResults, setRefWoSearchResults] = useState([]);
  const [refWoSearchLoading, setRefWoSearchLoading] = useState(false);
  const [showRefWoResults, setShowRefWoResults] = useState(false);
  const [selectedRefWo, setSelectedRefWo] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
    if (editWoId) {
      fetchWorkOrder();
    }
  }, [editWoId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="po-search-input"]') && 
          !e.target.closest('.po-search-dropdown')) {
        setShowPoResults(false);
      }
      if (!e.target.closest('[data-testid="ref-wo-search-input"]') && 
          !e.target.closest('.ref-wo-search-dropdown')) {
        setShowRefWoResults(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchCurrentUser = async () => {
    setUserLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    } finally {
      setUserLoading(false);
    }
  };

  const canCreateWO = currentUser?.role === 'admin' || ['weaving', 'marketing'].includes(currentUser?.department?.toLowerCase());

  const fetchWorkOrder = async () => {
    setFetchingWO(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${editWoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const wo = response.data;
      
      setFormData({
        customer_name: wo.customer_name || "",
        customer_po_number: wo.customer_po_number || "",
        shipment_date: wo.shipment_date ? wo.shipment_date.split('T')[0] : "",
        weaving_end_date: wo.weaving_end_date ? wo.weaving_end_date.split('T')[0] : "",
        ex_factory_date: wo.ex_factory_date ? wo.ex_factory_date.split('T')[0] : "",
        special_instructions: wo.special_instructions || "",
        packing_type: wo.packing_type || "packed"
      });
      
      // Load towel types
      if (wo.towel_types) {
        setTowelTypes({ ...defaultTowelTypes, ...wo.towel_types });
      }
      
      // Load order items (backward compatible with old size_items)
      if (wo.order_items && wo.order_items.length > 0) {
        const loadedItems = wo.order_items.map(item => ({
          ...emptyOrderItem,
          ...item,
          wt_per_pc_gms: item.wt_per_pc_gms?.toString() || "",
          finish_wt_per_pc_gms: item.finish_wt_per_pc_gms?.toString() || "",
          order_pcs: item.order_pcs?.toString() || "",
          order_kgs: item.order_kgs?.toString() || "",
          total_finish_wt_kgs: item.total_finish_wt_kgs?.toString() || "",
          extra_qty: item.extra_qty?.toString() || "",
          plan_pcs: item.plan_pcs?.toString() || ""
        }));
        // Recalculate any missing auto-fields
        setOrderItems(recalculateAllAutoFields(loadedItems));
      } else if (wo.size_items && wo.size_items.length > 0) {
        // Convert old format to new format
        const loadedItems = wo.size_items.map(item => ({
          design_name: item.quality || "",
          size_cm: item.size || "",
          gsm: item.gsm || "",
          wt_per_pc_gms: item.wt_per_pc_gms?.toString() || "",
          finish_wt_per_pc_gms: item.finish_wt_per_pc_gms?.toString() || "",
          order_pcs: item.qty_pcs?.toString() || "",
          order_kgs: item.qty_weight?.toString() || "",
          total_finish_wt_kgs: item.total_finish_wt_kgs?.toString() || "",
          pile_count: "",
          ground_count: "",
          weft: "",
          border: "",
          extra_qty: "",
          plan_pcs: item.qty_pcs?.toString() || "",
          remarks: ""
        }));
        // Recalculate any missing auto-fields
        setOrderItems(recalculateAllAutoFields(loadedItems));
      }
      
      // Load shade items and show section if items exist
      if (wo.shade_items && wo.shade_items.length > 0) {
        setShadeItems(wo.shade_items);
        setShowShadeDetails(true);
      }
      
      // Store original WO for edit restrictions
      setOriginalWO(wo);
    } catch (err) {
      console.error("Failed to fetch work order:", err);
      setError("Failed to fetch work order for editing");
    } finally {
      setFetchingWO(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTowelTypeChange = (key, value) => {
    setTowelTypes({ ...towelTypes, [key]: value });
  };

  // Order Items handlers
  const updateOrderItem = (index, field, value) => {
    const updated = [...orderItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate order_kgs if order_pcs and wt_per_pc_gms are filled
    if ((field === 'order_pcs' || field === 'wt_per_pc_gms') && updated[index].order_pcs && updated[index].wt_per_pc_gms) {
      const pcs = parseFloat(updated[index].order_pcs) || 0;
      const wtPerPc = parseFloat(updated[index].wt_per_pc_gms) || 0;
      updated[index].order_kgs = ((pcs * wtPerPc) / 1000).toFixed(2);
    }
    
    // Auto-calculate total_finish_wt_kgs if order_pcs and finish_wt_per_pc_gms are filled
    if ((field === 'order_pcs' || field === 'finish_wt_per_pc_gms') && updated[index].order_pcs && updated[index].finish_wt_per_pc_gms) {
      const pcs = parseFloat(updated[index].order_pcs) || 0;
      const finishWtPerPc = parseFloat(updated[index].finish_wt_per_pc_gms) || 0;
      updated[index].total_finish_wt_kgs = ((pcs * finishWtPerPc) / 1000).toFixed(2);
    }
    
    // Auto-calculate plan_pcs = order_pcs + extra_qty (supports % or number)
    if (field === 'order_pcs' || field === 'extra_qty') {
      const orderPcs = parseInt(updated[index].order_pcs) || 0;
      const extraQtyStr = updated[index].extra_qty?.toString() || '';
      
      let extraPcs = 0;
      if (extraQtyStr.includes('%')) {
        // Percentage calculation: e.g., "3%" or "3.0%" of order_pcs
        const percentage = parseFloat(extraQtyStr.replace('%', '')) || 0;
        extraPcs = Math.round((orderPcs * percentage) / 100);
      } else {
        // Direct number addition
        extraPcs = parseInt(extraQtyStr) || 0;
      }
      
      updated[index].plan_pcs = (orderPcs + extraPcs).toString();
    }
    
    // Auto-calculate plan_kgs = (plan_pcs × wt_per_pc_gms) / 1000
    if (field === 'order_pcs' || field === 'extra_qty' || field === 'wt_per_pc_gms') {
      const planPcs = parseInt(updated[index].plan_pcs) || 0;
      const wtPerPc = parseFloat(updated[index].wt_per_pc_gms) || 0;
      if (planPcs > 0 && wtPerPc > 0) {
        updated[index].plan_kgs = ((planPcs * wtPerPc) / 1000).toFixed(2);
      }
    }
    
    setOrderItems(updated);
  };

  // Recalculate all auto-fields for all items (called after data load or when needed)
  const recalculateAllAutoFields = (items) => {
    return items.map(item => {
      const updated = { ...item };
      const orderPcs = parseInt(item.order_pcs) || 0;
      const wtPerPc = parseFloat(item.wt_per_pc_gms) || 0;
      const finishWtPerPc = parseFloat(item.finish_wt_per_pc_gms) || 0;
      
      // Always recalculate order_kgs if inputs are valid (it's an auto-calculated field)
      if (orderPcs > 0 && wtPerPc > 0) {
        updated.order_kgs = ((orderPcs * wtPerPc) / 1000).toFixed(2);
      }
      
      // Always recalculate total_finish_wt_kgs if inputs are valid
      if (orderPcs > 0 && finishWtPerPc > 0) {
        updated.total_finish_wt_kgs = ((orderPcs * finishWtPerPc) / 1000).toFixed(2);
      }
      
      // Always recalculate plan_pcs = order_pcs + extra_qty
      if (orderPcs > 0) {
        const extraQtyStr = item.extra_qty?.toString() || '';
        let extraPcs = 0;
        if (extraQtyStr.includes('%')) {
          const percentage = parseFloat(extraQtyStr.replace('%', '')) || 0;
          extraPcs = Math.round((orderPcs * percentage) / 100);
        } else {
          extraPcs = parseInt(extraQtyStr) || 0;
        }
        updated.plan_pcs = (orderPcs + extraPcs).toString();
      }
      
      // Always recalculate plan_kgs = (plan_pcs × wt_per_pc_gms) / 1000
      const planPcs = parseInt(updated.plan_pcs) || 0;
      if (planPcs > 0 && wtPerPc > 0) {
        updated.plan_kgs = ((planPcs * wtPerPc) / 1000).toFixed(2);
      }
      
      return updated;
    });
  };

  const addOrderRow = () => {
    if (orderItems.length < 20) {
      setOrderItems([...orderItems, { ...emptyOrderItem }]);
    }
  };

  const removeOrderRow = (index) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    }
  };

  // PO Search function
  const searchPOs = async (query) => {
    if (!query || query.length < 2) {
      setPoSearchResults([]);
      setShowPoResults(false);
      return;
    }
    
    setPoSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/search/${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPoSearchResults(response.data.slice(0, 10)); // Limit to 10 results
      setShowPoResults(true);
    } catch (err) {
      console.error("PO search error:", err);
      setPoSearchResults([]);
    } finally {
      setPoSearchLoading(false);
    }
  };

  const handlePoSearchChange = (e) => {
    const query = e.target.value;
    setPoSearchQuery(query);
    
    // Debounce search
    clearTimeout(window.poSearchTimeout);
    window.poSearchTimeout = setTimeout(() => {
      searchPOs(query);
    }, 300);
  };

  const selectPO = (po) => {
    setLinkedPO(po);
    setFormData(prev => ({
      ...prev,
      customer_po_number: po.po_number,
      customer_name: prev.customer_name || po.vendor_name
    }));
    setPoSearchQuery("");
    setShowPoResults(false);
    setPoSearchResults([]);
  };

  const clearLinkedPO = () => {
    setLinkedPO(null);
    setFormData(prev => ({
      ...prev,
      customer_po_number: ""
    }));
  };

  // Reference WO Search - to copy data from existing Work Order
  const searchRefWorkOrders = async (query) => {
    if (!query || query.length < 2) {
      setRefWoSearchResults([]);
      setShowRefWoResults(false);
      return;
    }
    
    setRefWoSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter WOs that match the search query (by wo_number, customer_name, or customer_po_number)
      const filtered = response.data.filter(wo => 
        wo.wo_number?.toLowerCase().includes(query.toLowerCase()) ||
        wo.customer_name?.toLowerCase().includes(query.toLowerCase()) ||
        wo.customer_po_number?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10);
      setRefWoSearchResults(filtered);
      setShowRefWoResults(true);
    } catch (err) {
      console.error("Reference WO search error:", err);
      setRefWoSearchResults([]);
    } finally {
      setRefWoSearchLoading(false);
    }
  };

  const handleRefWoSearchChange = (e) => {
    const query = e.target.value;
    setRefWoSearchQuery(query);
    
    // Debounce search
    clearTimeout(window.refWoSearchTimeout);
    window.refWoSearchTimeout = setTimeout(() => {
      searchRefWorkOrders(query);
    }, 300);
  };

  const selectRefWorkOrder = (wo) => {
    setSelectedRefWo(wo);
    
    // Populate form with data from selected WO
    setFormData({
      customer_name: wo.customer_name || "",
      customer_po_number: wo.customer_po_number || "",
      shipment_date: wo.shipment_date ? wo.shipment_date.split('T')[0] : "",
      weaving_end_date: wo.weaving_end_date ? wo.weaving_end_date.split('T')[0] : "",
      ex_factory_date: wo.ex_factory_date ? wo.ex_factory_date.split('T')[0] : "",
      special_instructions: wo.special_instructions || "",
      packing_type: wo.packing_type || "packed"
    });
    
    // Load towel types
    if (wo.towel_types) {
      setTowelTypes({ ...defaultTowelTypes, ...wo.towel_types });
    }
    
    // Load order items
    if (wo.order_items && wo.order_items.length > 0) {
      setOrderItems(wo.order_items.map(item => ({
        ...emptyOrderItem,
        ...item,
        wt_per_pc_gms: item.wt_per_pc_gms?.toString() || "",
        finish_wt_per_pc_gms: item.finish_wt_per_pc_gms?.toString() || "",
        order_pcs: item.order_pcs?.toString() || "",
        order_kgs: item.order_kgs?.toString() || "",
        total_finish_wt_kgs: item.total_finish_wt_kgs?.toString() || "",
        extra_qty: item.extra_qty?.toString() || "",
        plan_pcs: item.plan_pcs?.toString() || "",
        plan_kgs: item.plan_kgs?.toString() || ""
      })));
    } else if (wo.size_items && wo.size_items.length > 0) {
      setOrderItems(wo.size_items.map(item => ({
        design_name: item.quality || "",
        size_cm: item.size || "",
        gsm: item.gsm || "",
        wt_per_pc_gms: item.wt_per_pc_gms?.toString() || "",
        finish_wt_per_pc_gms: item.finish_wt_per_pc_gms?.toString() || "",
        order_pcs: item.qty_pcs?.toString() || "",
        order_kgs: item.qty_weight?.toString() || "",
        total_finish_wt_kgs: item.total_finish_wt_kgs?.toString() || "",
        pile_count: "",
        ground_count: "",
        weft: "",
        border: "",
        extra_qty: "",
        plan_pcs: item.qty_pcs?.toString() || "",
        plan_kgs: "",
        remarks: ""
      })));
    }
    
    // Load shade items
    if (wo.shade_items && wo.shade_items.length > 0) {
      setShadeItems(wo.shade_items.map(item => ({
        ...emptyShadeItem,
        ...item,
        wt_per_pc_gms: item.wt_per_pc_gms?.toString() || "",
        finish_wt_per_pc_gms: item.finish_wt_per_pc_gms?.toString() || "",
        order_pcs: item.order_pcs?.toString() || "",
        order_kgs: item.order_kgs?.toString() || "",
        total_finish_wt_kgs: item.total_finish_wt_kgs?.toString() || ""
      })));
      setShowShadeDetails(true);
    }
    
    setRefWoSearchQuery("");
    setShowRefWoResults(false);
    setRefWoSearchResults([]);
  };

  const clearRefWorkOrder = () => {
    setSelectedRefWo(null);
    // Reset form to empty state
    setFormData({
      customer_name: "",
      customer_po_number: "",
      shipment_date: "",
      weaving_end_date: "",
      ex_factory_date: "",
      special_instructions: "",
      packing_type: "packed"
    });
    setTowelTypes({ ...defaultTowelTypes });
    setOrderItems([{ ...emptyOrderItem }]);
    setShadeItems([{ ...emptyShadeItem }]);
    setShowShadeDetails(false);
  };

  // Shade Items handlers
  const updateShadeItem = (index, field, value) => {
    const updated = [...shadeItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate order_kgs if order_pcs and wt_per_pc_gms are filled
    if ((field === 'order_pcs' || field === 'wt_per_pc_gms') && updated[index].order_pcs && updated[index].wt_per_pc_gms) {
      const pcs = parseFloat(updated[index].order_pcs) || 0;
      const wtPerPc = parseFloat(updated[index].wt_per_pc_gms) || 0;
      updated[index].order_kgs = ((pcs * wtPerPc) / 1000).toFixed(2);
    }
    
    // Auto-calculate total_finish_wt_kgs if order_pcs and finish_wt_per_pc_gms are filled
    if ((field === 'order_pcs' || field === 'finish_wt_per_pc_gms') && updated[index].order_pcs && updated[index].finish_wt_per_pc_gms) {
      const pcs = parseFloat(updated[index].order_pcs) || 0;
      const finishWtPerPc = parseFloat(updated[index].finish_wt_per_pc_gms) || 0;
      updated[index].total_finish_wt_kgs = ((pcs * finishWtPerPc) / 1000).toFixed(2);
    }
    
    setShadeItems(updated);
  };

  const addShadeRow = () => {
    if (shadeItems.length < 20) {
      setShadeItems([...shadeItems, { ...emptyShadeItem }]);
    }
  };

  const removeShadeRow = (index) => {
    if (shadeItems.length > 1) {
      setShadeItems(shadeItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const totalPcs = orderItems.reduce((sum, item) => sum + (parseInt(item.order_pcs) || 0), 0);
    const totalKgs = orderItems.reduce((sum, item) => sum + (parseFloat(item.order_kgs) || 0), 0);
    const totalFinishWtKgs = orderItems.reduce((sum, item) => sum + (parseFloat(item.total_finish_wt_kgs) || 0), 0);
    const totalPlanPcs = orderItems.reduce((sum, item) => sum + (parseInt(item.plan_pcs) || 0), 0);
    const totalPlanKgs = orderItems.reduce((sum, item) => sum + (parseFloat(item.plan_kgs) || 0), 0);
    return { 
      totalPcs, 
      totalKgs: totalKgs.toFixed(2), 
      totalFinishWtKgs: totalFinishWtKgs.toFixed(2),
      totalPlanPcs,
      totalPlanKgs: totalPlanKgs.toFixed(2)
    };
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const validOrderItems = orderItems.filter(item => item.design_name && item.order_pcs);
    
    // For drafts, we don't require order items
    if (!saveAsDraft && validOrderItems.length === 0) {
      setError("Please add at least one order item with Design Name and Order Pcs");
      setLoading(false);
      return;
    }

    const validShadeItems = shadeItems.filter(item => item.shade_no || item.order_pcs);

    try {
      const token = localStorage.getItem('token');
      
      // Convert order_items to size_items for backward compatibility
      const sizeItems = validOrderItems.map(item => ({
        size: item.size_cm,
        quality: item.design_name,
        gsm: item.gsm,
        qty_pcs: parseInt(item.order_pcs) || 0,
        qty_weight: parseFloat(item.order_kgs) || 0,
        wt_per_pc_gms: parseFloat(item.wt_per_pc_gms) || 0,
        finish_wt_per_pc_gms: parseFloat(item.finish_wt_per_pc_gms) || 0
      }));
      
      const payload = {
        ...formData,
        status: saveAsDraft ? 'draft' : 'active',
        size_items: sizeItems,
        towel_types: towelTypes,
        order_items: validOrderItems.map(item => ({
          ...item,
          order_pcs: parseInt(item.order_pcs) || 0,
          order_kgs: parseFloat(item.order_kgs) || 0,
          total_finish_wt_kgs: parseFloat(item.total_finish_wt_kgs) || 0,
          plan_pcs: parseInt(item.plan_pcs) || 0,
          plan_kgs: parseFloat(item.plan_kgs) || 0,
          wt_per_pc_gms: parseFloat(item.wt_per_pc_gms) || 0,
          finish_wt_per_pc_gms: parseFloat(item.finish_wt_per_pc_gms) || 0
        })),
        shade_items: validShadeItems.map(item => ({
          ...item,
          order_pcs: parseInt(item.order_pcs) || 0,
          order_kgs: parseFloat(item.order_kgs) || 0,
          wt_per_pc_gms: parseFloat(item.wt_per_pc_gms) || 0,
          finish_wt_per_pc_gms: parseFloat(item.finish_wt_per_pc_gms) || 0,
          total_finish_wt_kgs: parseFloat(item.total_finish_wt_kgs) || 0
        }))
      };

      if (isEditMode) {
        await axios.put(`${API}/work-orders/${editWoId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert(`✅ Work Order ${saveAsDraft ? 'Saved as Draft' : 'Updated'} Successfully`);
      } else {
        const response = await axios.post(`${API}/work-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert(`✅ Work Order ${saveAsDraft ? 'Draft Saved' : 'Created'}: ${response.data.wo_number}`);
      }
      
      navigate('/weaving-production');
    } catch (err) {
      console.error("Failed to save work order:", err);
      setError(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} work order`);
    } finally {
      setLoading(false);
    }
  };

  const { totalPcs, totalKgs, totalFinishWtKgs, totalPlanPcs, totalPlanKgs } = calculateTotals();

  if (userLoading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (fetchingWO) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading work order...</div>
      </div>
    );
  }

  if (!canCreateWO && !isEditMode) {
    return (
      <div className="p-8">
        <Card className="max-w-lg mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-12 h-12 text-yellow-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground text-center">Only Admin, Weaving, and Marketing departments can create Work Orders</p>
            <Button className="mt-4" onClick={() => navigate('/')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="min-h-[44px]">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl text-primary">
              {isEditMode ? 'Edit Work Order' : 'Create Work Order'}
            </h1>
            <p className="text-muted-foreground">Weaving department work order form</p>
          </div>
        </div>
        
        {/* Download buttons - only show in edit mode */}
        {isEditMode && originalWO && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const response = await axios.get(`${API}/export/work-order/${originalWO.id}/document/pdf`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `${originalWO.wo_number}_WorkOrder.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                } catch (err) {
                  console.error("Failed to download PDF:", err);
                  setError("Failed to download PDF");
                }
              }}
              data-testid="download-wo-pdf"
            >
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-green-600 border-green-200 hover:bg-green-50"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const response = await axios.get(`${API}/export/work-order/${originalWO.id}/document/excel`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `${originalWO.wo_number}_WorkOrder.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                } catch (err) {
                  console.error("Failed to download Excel:", err);
                  setError("Failed to download Excel");
                }
              }}
              data-testid="download-wo-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Excel
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Reference WO Card - Optional copy from existing */}
        {!isEditMode && (
          <Card className="border-dashed border-blue-300 bg-blue-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                <Search className="w-4 h-4" />
                Reference Work Order (Optional)
              </CardTitle>
              <CardDescription className="text-xs">
                Search and select an existing Work Order to copy its data. You can modify any field after selection.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <Input
                  value={refWoSearchQuery}
                  onChange={handleRefWoSearchChange}
                  onFocus={() => refWoSearchResults.length > 0 && setShowRefWoResults(true)}
                  placeholder="Search by WO number, customer name, or PO number..."
                  className="pl-9"
                  data-testid="ref-wo-search-input"
                />
                {refWoSearchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
                
                {/* Reference WO Search Results Dropdown */}
                {showRefWoResults && refWoSearchResults.length > 0 && (
                  <div className="ref-wo-search-dropdown absolute left-0 right-0 z-50 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {refWoSearchResults.map((wo) => (
                      <div
                        key={wo.id}
                        onClick={() => selectRefWorkOrder(wo)}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{wo.wo_number}</div>
                            <div className="text-xs text-muted-foreground truncate">{wo.customer_name}</div>
                            <div className="text-xs text-muted-foreground truncate">PO: {wo.customer_po_number}</div>
                          </div>
                          <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">
                            {wo.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Reference WO Badge */}
              {selectedRefWo && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="default" className="flex items-center gap-1 bg-blue-600">
                    Copied from: {selectedRefWo.wo_number}
                    <X 
                      className="w-3 h-3 cursor-pointer hover:text-red-300" 
                      onClick={clearRefWorkOrder}
                    />
                  </Badge>
                  <span className="text-xs text-muted-foreground">Data loaded - modify as needed</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order Information
            </CardTitle>
            {canOnlyEditWeight && (
              <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mt-1">
                ⚠️ This Work Order is finalized. You can only edit Weight/PC fields. Contact admin for other changes.
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  placeholder="Enter customer name"
                  required
                  disabled={canOnlyEditWeight}
                  className={canOnlyEditWeight ? 'bg-muted/50' : ''}
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2 relative">
                <Label>Search & Link PO</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={poSearchQuery}
                    onChange={handlePoSearchChange}
                    onFocus={() => poSearchResults.length > 0 && setShowPoResults(true)}
                    placeholder="Search PO by number, vendor..."
                    className={`pl-9 ${canOnlyEditWeight ? 'bg-muted/50' : ''}`}
                    disabled={canOnlyEditWeight}
                    data-testid="po-search-input"
                  />
                  {poSearchLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {showPoResults && poSearchResults.length > 0 && (
                  <div className="po-search-dropdown absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {poSearchResults.map((po) => (
                      <div
                        key={po.id}
                        onClick={() => selectPO(po)}
                        className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-sm">{po.po_number}</div>
                            <div className="text-xs text-muted-foreground">{po.vendor_name}</div>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {po.status}
                          </Badge>
                        </div>
                        {po.items && po.items.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {po.items.map(i => i.product_name).slice(0, 2).join(', ')}
                            {po.items.length > 2 && ` +${po.items.length - 2} more`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Linked PO Badge */}
                {linkedPO && (
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="secondary" className="flex items-center gap-1">
                      Linked: {linkedPO.po_number}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-500" 
                        onClick={clearLinkedPO}
                      />
                    </Badge>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Customer PO Number *</Label>
                <Input
                  name="customer_po_number"
                  value={formData.customer_po_number}
                  onChange={handleChange}
                  placeholder="e.g., PO-276 Nautica"
                  required
                  disabled={canOnlyEditWeight}
                  className={canOnlyEditWeight ? 'bg-muted/50' : ''}
                  data-testid="customer-po-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Shipment Date *</Label>
                <Input
                  type="date"
                  name="shipment_date"
                  value={formData.shipment_date}
                  onChange={handleChange}
                  required
                  disabled={canOnlyEditWeight}
                  className={canOnlyEditWeight ? 'bg-muted/50' : ''}
                  data-testid="shipment-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Weaving End Date</Label>
                <Input
                  type="date"
                  name="weaving_end_date"
                  value={formData.weaving_end_date}
                  onChange={handleChange}
                  disabled={canOnlyEditWeight}
                  className={canOnlyEditWeight ? 'bg-muted/50' : ''}
                  data-testid="weaving-end-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>EX Factory Date</Label>
                <Input
                  type="date"
                  name="ex_factory_date"
                  value={formData.ex_factory_date}
                  onChange={handleChange}
                  disabled={canOnlyEditWeight}
                  className={canOnlyEditWeight ? 'bg-muted/50' : ''}
                  data-testid="ex-factory-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Special Instructions</Label>
                <Input
                  name="special_instructions"
                  value={formData.special_instructions}
                  onChange={handleChange}
                  placeholder="Any special notes"
                  disabled={canOnlyEditWeight}
                  className={canOnlyEditWeight ? 'bg-muted/50' : ''}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Type of Towel Card */}
        <Card>
          <CardHeader>
            <CardTitle>Type of Towel</CardTitle>
            <CardDescription>Select applicable towel characteristics (Yes/No)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4">
              {[
                { key: 'piece_dyed', label: 'Piece Dyed' },
                { key: 'yarn_dyed', label: 'Yarn Dyed' },
                { key: 'wash_finish', label: 'Wash Finish' },
                { key: 'loom_state', label: 'Loom State' },
                { key: 'dobby', label: 'Dobby' },
                { key: 'jacquard', label: 'Jacquard' },
                { key: 'sheared', label: 'Sheared' },
                { key: 'un_sheared', label: 'Un-Sheared' },
              ].map(({ key, label }) => (
                <div key={key} className="flex flex-col items-center space-y-2 p-2 border rounded-lg">
                  <Label className="text-xs text-center font-medium">{label}</Label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleTowelTypeChange(key, true)}
                      className={`px-2 py-1 text-xs rounded ${towelTypes[key] === true ? 'bg-green-500 text-white' : 'bg-gray-100'}`}
                    >
                      YES
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTowelTypeChange(key, false)}
                      className={`px-2 py-1 text-xs rounded ${towelTypes[key] === false ? 'bg-red-500 text-white' : 'bg-gray-100'}`}
                    >
                      NO
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex flex-col space-y-2 p-2 border rounded-lg">
                <Label className="text-xs font-medium">Other</Label>
                <Input
                  className="h-8 text-xs"
                  value={towelTypes.other}
                  onChange={(e) => handleTowelTypeChange('other', e.target.value)}
                  placeholder="Specify"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packing Type Card */}
        <Card>
          <CardHeader>
            <CardTitle>Packing Type</CardTitle>
            <CardDescription>Select how the order will be packed after production</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFormData({...formData, packing_type: "packed"})}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  formData.packing_type === "packed" 
                    ? 'border-green-500 bg-green-50 text-green-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="packing-packed"
              >
                <div className="text-center">
                  <div className={`text-2xl mb-2 ${formData.packing_type === "packed" ? 'text-green-600' : 'text-gray-400'}`}>📦</div>
                  <div className="font-bold">Packed</div>
                  <div className="text-xs text-muted-foreground mt-1">Weaving → Dyeing → Stitching</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, packing_type: "roll_form"})}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  formData.packing_type === "roll_form" 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="packing-roll-form"
              >
                <div className="text-center">
                  <div className={`text-2xl mb-2 ${formData.packing_type === "roll_form" ? 'text-blue-600' : 'text-gray-400'}`}>🧻</div>
                  <div className="font-bold">Roll Form</div>
                  <div className="text-xs text-muted-foreground mt-1">Weaving → Dyeing (No Stitching)</div>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Details of Order Qty Card */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Details of Order Quantity</CardTitle>
                <CardDescription>Add design items with specifications (max 20 items)</CardDescription>
              </div>
              <Button type="button" variant="outline" onClick={addOrderRow} disabled={orderItems.length >= 20} className="min-h-[44px]">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="min-w-[1900px]">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-sm font-bold min-w-[180px] whitespace-nowrap">Design Name *</TableHead>
                    <TableHead className="text-sm font-bold min-w-[120px] whitespace-nowrap">Size (CM)</TableHead>
                    <TableHead className="text-sm font-bold min-w-[80px] whitespace-nowrap">GSM</TableHead>
                    <TableHead className={`text-sm font-bold min-w-[120px] whitespace-nowrap ${canOnlyEditWeight ? 'bg-yellow-100' : ''}`}>Wt/Pc (gms) {canOnlyEditWeight && '✏️'}</TableHead>
                    <TableHead className="text-sm font-bold min-w-[130px] whitespace-nowrap">Finish Wt/Pc (gms)</TableHead>
                    <TableHead className="text-sm font-bold min-w-[120px] whitespace-nowrap">Order Pcs *</TableHead>
                    <TableHead className="text-sm font-bold min-w-[120px] whitespace-nowrap">Order Kgs</TableHead>
                    <TableHead className="text-sm font-bold min-w-[140px] whitespace-nowrap bg-green-50">Total Finish Wt (Kgs)</TableHead>
                    <TableHead className="text-sm font-bold min-w-[140px] whitespace-nowrap">Pile Count</TableHead>
                    <TableHead className="text-sm font-bold min-w-[140px] whitespace-nowrap">Ground Count</TableHead>
                    <TableHead className="text-sm font-bold min-w-[100px] whitespace-nowrap">Weft</TableHead>
                    <TableHead className="text-sm font-bold min-w-[150px] whitespace-nowrap">Border</TableHead>
                    <TableHead className="text-sm font-bold min-w-[120px] whitespace-nowrap">Extra Qty</TableHead>
                    <TableHead className="text-sm font-bold min-w-[120px] whitespace-nowrap bg-blue-50">Plan Pcs</TableHead>
                    <TableHead className="text-sm font-bold min-w-[120px] whitespace-nowrap bg-blue-50">Plan Kgs</TableHead>
                    <TableHead className="text-sm font-bold min-w-[150px] whitespace-nowrap">Remarks</TableHead>
                    <TableHead className="text-sm font-bold w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Input
                          className={`h-10 text-sm min-w-[160px] ${canOnlyEditWeight ? 'bg-muted/50' : ''}`}
                          value={item.design_name}
                          onChange={(e) => updateOrderItem(index, 'design_name', e.target.value)}
                          placeholder="Vintage Crew"
                          disabled={canOnlyEditWeight}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className={`h-10 text-sm min-w-[100px] ${canOnlyEditWeight ? 'bg-muted/50' : ''}`}
                          value={item.size_cm}
                          onChange={(e) => updateOrderItem(index, 'size_cm', e.target.value)}
                          placeholder="40x60"
                          disabled={canOnlyEditWeight}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className={`h-10 text-sm min-w-[70px] ${canOnlyEditWeight ? 'bg-muted/50' : ''}`}
                          value={item.gsm}
                          onChange={(e) => updateOrderItem(index, 'gsm', e.target.value)}
                          placeholder="430"
                          disabled={canOnlyEditWeight}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          className={`h-10 text-sm min-w-[100px] ${canOnlyEditWeight ? 'bg-yellow-50 border-yellow-300' : ''}`}
                          value={item.wt_per_pc_gms}
                          onChange={(e) => updateOrderItem(index, 'wt_per_pc_gms', e.target.value)}
                          placeholder=""
                          title={canOnlyEditWeight ? "You can edit this field" : ""}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          className={`h-10 text-sm min-w-[110px] ${canOnlyEditWeight ? 'bg-muted/50' : ''}`}
                          value={item.finish_wt_per_pc_gms}
                          onChange={(e) => updateOrderItem(index, 'finish_wt_per_pc_gms', e.target.value)}
                          placeholder="95"
                          disabled={canOnlyEditWeight}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className={`h-10 text-sm min-w-[100px] ${canOnlyEditWeight ? 'bg-muted/50' : ''}`}
                          value={item.order_pcs}
                          onChange={(e) => updateOrderItem(index, 'order_pcs', e.target.value)}
                          placeholder="2000"
                          disabled={canOnlyEditWeight}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-10 text-sm min-w-[100px] bg-muted/30"
                          value={item.order_kgs}
                          onChange={(e) => updateOrderItem(index, 'order_kgs', e.target.value)}
                          placeholder="Auto"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-10 text-sm min-w-[120px] bg-green-50 font-medium"
                          value={item.total_finish_wt_kgs}
                          onChange={(e) => updateOrderItem(index, 'total_finish_wt_kgs', e.target.value)}
                          placeholder="Auto"
                          readOnly
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-10 text-sm min-w-[120px]"
                          value={item.pile_count}
                          onChange={(e) => updateOrderItem(index, 'pile_count', e.target.value)}
                          placeholder="16/80 ZT"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-10 text-sm min-w-[120px]"
                          value={item.ground_count}
                          onChange={(e) => updateOrderItem(index, 'ground_count', e.target.value)}
                          placeholder="2*26"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-10 text-sm min-w-[90px]"
                          value={item.weft}
                          onChange={(e) => updateOrderItem(index, 'weft', e.target.value)}
                          placeholder="12s OE"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-10 text-sm min-w-[130px]"
                          value={item.border}
                          onChange={(e) => updateOrderItem(index, 'border', e.target.value)}
                          placeholder="AS PER SAMPLE"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-10 text-sm min-w-[100px]"
                          value={item.extra_qty}
                          onChange={(e) => updateOrderItem(index, 'extra_qty', e.target.value)}
                          placeholder="3% or 60"
                          title="Enter percentage (e.g., 3%) or direct number (e.g., 60)"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="h-10 text-sm min-w-[100px] bg-blue-50 font-semibold"
                          value={item.plan_pcs}
                          readOnly
                          placeholder="Auto"
                          title="Auto-calculated: Order Pcs + Extra Qty"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-10 text-sm min-w-[100px] bg-blue-50 font-semibold"
                          value={item.plan_kgs}
                          readOnly
                          placeholder="Auto"
                          title="Auto-calculated: Plan Pcs × Wt/PC / 1000"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="h-9 text-sm"
                          value={item.remarks}
                          onChange={(e) => updateOrderItem(index, 'remarks', e.target.value)}
                          placeholder="Notes"
                        />
                      </TableCell>
                      <TableCell>
                        {orderItems.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOrderRow(index)}
                            className="h-9 w-9 p-0 text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell className="text-right" colSpan={5}>Totals:</TableCell>
                    <TableCell className="text-primary font-bold">{totalPcs.toLocaleString()} pcs</TableCell>
                    <TableCell className="text-primary font-bold">{totalKgs} KG</TableCell>
                    <TableCell className="text-green-600 font-bold bg-green-50">{totalFinishWtKgs} KG</TableCell>
                    <TableCell colSpan={5}></TableCell>
                    <TableCell className="text-primary font-bold bg-blue-50">{totalPlanPcs.toLocaleString()} pcs</TableCell>
                    <TableCell className="text-primary font-bold bg-blue-50">{totalPlanKgs} KG</TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Shade Details Toggle Button */}
        {!showShadeDetails && (
          <div className="flex justify-center">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowShadeDetails(true)}
              className="min-h-[44px]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Shade Details (Optional)
            </Button>
          </div>
        )}

        {/* Shade/Shade No Card - Only visible when showShadeDetails is true */}
        {showShadeDetails && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Shade Details</CardTitle>
                  <CardDescription>Add shade/color breakdown</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={addShadeRow} disabled={shadeItems.length >= 20} className="min-h-[44px]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Shade
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={() => {
                      setShowShadeDetails(false);
                      setShadeItems([{ ...emptyShadeItem }]);
                    }}
                    className="min-h-[44px] text-muted-foreground"
                  >
                    Hide
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-bold">Shade/Shade No</TableHead>
                      <TableHead className="text-xs font-bold">Size</TableHead>
                      <TableHead className="text-xs font-bold">Wt/PC (gms)</TableHead>
                      <TableHead className="text-xs font-bold">Finish Wt/PC (gms)</TableHead>
                      <TableHead className="text-xs font-bold">Order Pcs</TableHead>
                      <TableHead className="text-xs font-bold">Order Kgs</TableHead>
                      <TableHead className="text-xs font-bold bg-green-50">Total Finish Wt (Kgs)</TableHead>
                      <TableHead className="text-xs font-bold w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shadeItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Input
                            className="h-9 text-sm"
                            value={item.shade_no}
                            onChange={(e) => updateShadeItem(index, 'shade_no', e.target.value)}
                            placeholder="e.g., Navy Blue"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-9 text-sm"
                            value={item.size}
                            onChange={(e) => updateShadeItem(index, 'size', e.target.value)}
                            placeholder="40x60"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-9 text-sm w-20"
                            value={item.wt_per_pc_gms}
                            onChange={(e) => updateShadeItem(index, 'wt_per_pc_gms', e.target.value)}
                            placeholder="270"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-9 text-sm w-20"
                            value={item.finish_wt_per_pc_gms}
                            onChange={(e) => updateShadeItem(index, 'finish_wt_per_pc_gms', e.target.value)}
                            placeholder="250"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            className="h-9 text-sm w-20"
                            value={item.order_pcs}
                            onChange={(e) => updateShadeItem(index, 'order_pcs', e.target.value)}
                            placeholder="500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-9 text-sm w-20 bg-muted/30"
                            value={item.order_kgs}
                            readOnly
                            placeholder="Auto"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-9 text-sm w-20 bg-green-50"
                            value={item.total_finish_wt_kgs}
                            readOnly
                            placeholder="Auto"
                          />
                        </TableCell>
                        <TableCell>
                          {shadeItems.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeShadeRow(index)}
                              className="h-9 w-9 p-0 text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="min-h-[44px] w-full sm:w-auto order-last sm:order-first">
            Cancel
          </Button>
          <Button 
            type="button" 
            variant="secondary" 
            disabled={loading} 
            onClick={(e) => handleSubmit(e, true)} 
            className="min-h-[44px] w-full sm:w-auto sm:min-w-[130px]"
            data-testid="save-draft-btn"
          >
            <FileEdit className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button type="submit" disabled={loading} className="min-h-[44px] w-full sm:w-auto sm:min-w-[150px]" data-testid="submit-wo-btn">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : (isEditMode ? 'Update Work Order' : 'Create Work Order')}
          </Button>
        </div>
      </form>
    </div>
  );
}
